/**
 * REJOINDRE UN RÉSEAU — dix gemmes, une seule fois (ROADMAP 2.6).
 *
 * ══ Ce qu'on peut vérifier, et ce qu'on ne peut pas ═════════════════════════
 *
 * ⚠️ **On ne peut PAS savoir qu'un joueur a rejoint un Discord.** La ROADMAP annonçait
 * « vérifié côté serveur via `social-stats` » — or `social-stats` rend des **compteurs
 * d'abonnés**, pas l'appartenance d'une personne. Le vérifier vraiment demanderait un
 * OAuth Discord, YouTube et TikTok, c'est-à-dire trois intégrations et trois écrans de
 * consentement pour dix gemmes.
 *
 * Ce qu'on observe réellement, c'est **l'ouverture du lien**. La récompense paie donc ça,
 * et le libellé le dit — « découvre » plutôt que « rejoins ». Promettre une vérification
 * qui n'existe pas serait pire que de ne rien vérifier : le joueur qui rejoint vraiment se
 * sentirait floué de ne rien recevoir de plus.
 *
 * ══ Pourquoi ce n'est pas fermable, et pourquoi ça n'a pas d'importance ═════
 *
 * Un joueur peut ouvrir le lien, refermer, et garder ses gemmes. Il peut même vider son
 * stockage local pour recommencer — mais **ça lui coûte sa ville entière**, puisque le
 * compteur voyage dans le snapshot. Trente gemmes contre une partie : le calcul se fait
 * tout seul.
 *
 * C'est la même logique que le §14 : on ne se protège pas d'un abus qui coûte plus cher
 * qu'il ne rapporte.
 *
 * ══ Le montant, et son cadre (G§0) ═════════════════════════════════════════
 *
 * Trente gemmes au total, une fois par partie — soit **une journée de revenu** au rythme
 * mesuré (~29 gemmes/jour). Assez pour se payer une accélération tout de suite, trop peu
 * pour déplacer l'équilibre d'une ère (capacité mesurée : 1 394 gemmes).
 *
 * Et la Loi 1 tient : ce sont des gemmes GRATUITES. Un joueur qui ne paie jamais les
 * obtient exactement comme les autres.
 */

/** Les réseaux récompensés. Un ajout ici suffit — le reste s'en déduit. */
export const SOCIAL_CHANNELS = ['discord', 'youtube', 'tiktok'] as const;

export type SocialChannel = (typeof SOCIAL_CHANNELS)[number];

/**
 * Gemmes par réseau.
 *
 * Dix : le tiers d'une accélération, donc trois réseaux valent exactement une. C'est
 * lisible sans calcul — « tout faire, c'est un boost » — et un nombre rond se retient.
 */
export const SOCIAL_REWARD_GEMS = 10;

/**
 * Identifiant d'octroi, rangé dans `claimedMilestones`.
 *
 * Même véhicule que la récompense de renommage : la liste est déjà persistée, déjà relue à
 * la sauvegarde, déjà synchronisée, et **idempotente par construction**. Un second octroi
 * est impossible sans qu'on ait à y penser — et il voyage d'un appareil à l'autre, donc
 * réclamer deux fois sur deux téléphones ne marche pas non plus.
 */
export function socialClaimId(channel: SocialChannel): string {
  return `social-${channel}`;
}

/** Ce réseau a-t-il déjà été récompensé ? */
export function hasClaimedSocial(claimed: readonly string[], channel: SocialChannel): boolean {
  return claimed.includes(socialClaimId(channel));
}

/** Combien de gemmes restent à prendre, tous réseaux confondus. */
export function remainingSocialGems(claimed: readonly string[]): number {
  return (
    SOCIAL_CHANNELS.filter((channel) => !hasClaimedSocial(claimed, channel)).length *
    SOCIAL_REWARD_GEMS
  );
}
